import * as React from "react";
import { Link, Outlet, useBlocker, useParams, useSearchParams } from "react-router-dom";
import { AlertCircle, Loader2 } from "lucide-react";

import { usePortal } from "@/features/portal/portal-context";
import { editTarget, landingPath } from "@/features/portal/navigation";
import { PopinMark } from "@/components/popin-mark";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type ResolveState = "resolving" | "ready" | "notfound";

/** Centered spinner while a brand is being resolved from the URL. */
function BrandResolving() {
  return (
    <div className="grid min-h-[60vh] place-items-center">
      <div className="flex flex-col items-center gap-4">
        <PopinMark size={30} />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Opening brand…
        </div>
      </div>
    </div>
  );
}

/** Shown when `:brandId` doesn't resolve — unknown id, or one the viewer can't
 *  reach. Replaces the old silent bounce so a stale/forbidden link is legible. */
function BrandNotFound() {
  const { brands, canCreateBrand, isSuperAdmin } = usePortal();
  const home = landingPath({ brands, canCreateBrand, isSuperAdmin });
  return (
    <div className="mx-auto max-w-[460px] py-16 text-center">
      <div className="mx-auto mb-4 grid size-12 place-items-center rounded-2xl border border-border bg-card">
        <AlertCircle className="size-6 text-[#ff7a7a]" />
      </div>
      <h1 className="mb-1.5 text-xl font-extrabold tracking-[-.02em]">Brand not found</h1>
      <p className="mb-6 text-sm text-muted-foreground">
        This brand doesn’t exist, or you don’t have access to it. If someone shared the link, ask
        them to confirm it — or that you’ve been added as a member.
      </p>
      <div className="flex items-center justify-center gap-2.5">
        <Button asChild>
          <Link to={home}>Back to the portal</Link>
        </Button>
        {isSuperAdmin && (
          <Button variant="subtle" asChild>
            <Link to="/admin/brands">All brands</Link>
          </Button>
        )}
      </div>
    </div>
  );
}

/** Modal asking whether to discard unsaved edits before a blocked navigation. */
function UnsavedChangesDialog({ onKeep, onDiscard }: { onKeep: () => void; onDiscard: () => void }) {
  return (
    <Dialog open onOpenChange={(open) => !open && onKeep()}>
      <DialogContent showCloseButton={false} className="max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Discard unsaved changes?</DialogTitle>
          <DialogDescription>
            You have edits that haven’t been published. Leaving now will discard them.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onKeep}>
            Keep editing
          </Button>
          <Button onClick={onDiscard}>Discard changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Layout for the brand editors (`/b/:brandId/...`). The URL is the source of
 * truth for what's being edited: this resolves `:brandId` into local state,
 * applies the `:brandId` + `?theme` selection into the draft, and guards against
 * losing unsaved edits when a navigation would change *what* is edited.
 *
 * Replaces the old `RequireBrand` guard and the in-memory `openBrandAsAdmin`.
 */
export function BrandRoute() {
  const { brandId = "" } = useParams();
  const [searchParams] = useSearchParams();
  const themeId = searchParams.get("theme") ?? undefined;
  const { resolveBrand, applySelection, dirty } = usePortal();

  const [state, setState] = React.useState<ResolveState>("resolving");

  // Resolve the brand whenever the path's brand changes (find-or-fetch). A theme
  // change alone doesn't re-resolve, so switching themes shows no loading flash.
  React.useEffect(() => {
    let cancelled = false;
    setState("resolving");
    resolveBrand(brandId).then((ok) => {
      if (!cancelled) setState(ok ? "ready" : "notfound");
    });
    return () => {
      cancelled = true;
    };
  }, [brandId, resolveBrand]);

  // Once resolved, mirror the URL selection (brand + theme) into the draft.
  React.useEffect(() => {
    if (state === "ready") applySelection(brandId, themeId);
  }, [state, brandId, themeId, applySelection]);

  // Unsaved-changes guard: block a navigation that would change the edited brand
  // or theme (or leave the editors entirely) while the draft is dirty. Section
  // changes within the same brand+theme pass through freely.
  const blocker = useBlocker(
    React.useCallback(
      ({ currentLocation, nextLocation }) => {
        if (!dirty) return false;
        const cur = editTarget(currentLocation);
        const next = editTarget(nextLocation);
        if (!next.inEditor) return true;
        return cur.brandId !== next.brandId || cur.themeId !== next.themeId;
      },
      [dirty],
    ),
  );

  if (state === "resolving") return <BrandResolving />;
  if (state === "notfound") return <BrandNotFound />;

  return (
    <>
      <Outlet />
      {blocker.state === "blocked" && (
        <UnsavedChangesDialog onKeep={() => blocker.reset()} onDiscard={() => blocker.proceed()} />
      )}
    </>
  );
}
